/* eslint-disable @typescript-eslint/no-explicit-any */
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { MenuButton } from '@juvare/common-web-ui/MenuButton';
import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { Image } from '@juvare/platform-sdk-react/ui/components';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useAttachmentClient, useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import { downloadFile } from '@juvare/platform-sdk-react/ui/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Alert, Box, Button, CircularProgress, Grid, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { fileVersionEntityName } from '../../../../backend/features/file-manager';
import { allowCheckIn, allowWrite, getDefaultPermissions } from '../../../../backend/features/permissions';
import { showArchiveDialog, showCheckInProgressDialog, showCheckOutDialog, showMoveDialog } from '../../../components';
import { showCopyDialog } from '../../../components/dialogs/CopyDialog';
import { useDocumentLibraryStorage, useDocumentLibraryTranslations, useHighestPermissionText } from '../../../hooks';
import { useEditDialogMenuItem } from '../../../hooks/useEditDialogMenuItem';
import { acceptedFileExtensions, humanReadableFileSize } from '../../../utils';
export const fileManagerSlideoutMenuItemsIds = {
    downloadFile: 'download-file',
    editDetails: 'edit-details',
    manageAccess: 'manage-access',
    fileHistory: 'file-history',
    archive: 'archive',
    move: 'move',
    copyFile: 'copy-file',
};
export function FileManagerSlideout(props) {
    const [fileResponse, setFileResponse] = React.useState();
    const storage = useDocumentLibraryStorage();
    React.useEffect(() => {
        storage.fileStorageClient.get(props.fileId).then(setFileResponse).catch(handleErrorDialog);
    }, [props.fileId, storage.fileStorageClient]);
    if (!fileResponse) {
        return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(CircularProgress, null)));
    }
    return React.createElement(FileManagerSlideoutContent, Object.assign({}, props, { fileResponse: fileResponse }));
}
function FileManagerSlideoutContent(props) {
    const { t } = useDocumentLibraryTranslations();
    const downXsScreen = useMediaQuery((th) => th.breakpoints.down('sm'));
    const userState = useUserState();
    const inputFile = React.useRef(null);
    const storage = useDocumentLibraryStorage();
    const attachment = useAttachmentClient();
    const [children, setChildren] = React.useState();
    const [previewImageKey, setPreviewImageKey] = React.useState(Date.now());
    const [metadata, setMetadata] = React.useState();
    const [attachmentError, setAttachmentError] = React.useState();
    const canWrite = props.fileResponse.permissions.includes('write');
    const canModify = props.fileResponse.permissions.includes('modify');
    const highestPermission = useHighestPermissionText(props.fileResponse.permissions);
    const defaultPermissions = getDefaultPermissions();
    const functionClient = useFunctionClient();
    const fileEntityType = props.fileResponse.columns.directory ? t('common.folder') : t('common.file');
    const supportedFileTypesSetting = useSelector(getSettings).supportedFileTypes.supportedFileTypes;
    const allExtensions = acceptedFileExtensions(supportedFileTypesSetting);
    const editDialogMenuItem = useEditDialogMenuItem();
    React.useEffect(() => {
        if (props.fileResponse.columns.directory) {
            storage.listFiles({ parentId: props.fileResponse.id }).then(setChildren).catch(handleErrorSnackbar);
        }
        else {
            setChildren(undefined);
        }
    }, [storage, props.fileResponse]);
    React.useEffect(() => {
        if (!props.fileResponse.columns.directory && props.fileResponse.columns.currentVersionId) {
            attachment
                .getMetadata({ entity: fileVersionEntityName, entityId: props.fileResponse.columns.currentVersionId, key: 'data' })
                .then((response) => {
                setMetadata(response);
                if (response.virusScanResult === 'infected') {
                    setAttachmentError('Virus scan found file to be harmful.');
                }
                else {
                    setAttachmentError(undefined);
                }
            })
                .catch(() => {
                setAttachmentError('File upload failed or is still in progress.');
            });
        }
        else {
            setAttachmentError(undefined);
            setMetadata(undefined);
        }
    }, [attachment, props.fileResponse]);
    const menuItems = React.useMemo(() => {
        const menuItems = [];
        const editMenuItem = editDialogMenuItem({ fileColumns: props.fileResponse, fileEntityType, onEditDetails: props.onEditDetails });
        if (props.fileResponse.columns.directory) {
            if (canModify) {
                menuItems.push(editMenuItem);
                menuItems.push(createMoveMenuItem({
                    t,
                    fileEntityType,
                    fileData: props.fileResponse,
                    onReload: props.onReload,
                    storage: storage.fileStorageClient,
                    userState: userState,
                    defaultPermissions,
                }));
                if (props.onManagePermissions) {
                    menuItems.push(createPermissionsMenuItem({
                        onManagePermissions: props.onManagePermissions,
                        fileData: props.fileResponse,
                        onReload: props.onReload,
                        t,
                    }));
                }
                menuItems.push(createArchiveFileMenuItem({
                    t,
                    functionClient,
                    file: props.fileResponse,
                    onSuccess: props.onReload,
                }));
            }
        }
        else {
            if (canModify) {
                if (!attachmentError) {
                    menuItems.push(editMenuItem);
                    menuItems.push(createMoveMenuItem({
                        t,
                        fileEntityType,
                        fileData: props.fileResponse,
                        onReload: props.onReload,
                        storage: storage.fileStorageClient,
                        userState: userState,
                        defaultPermissions,
                    }));
                    menuItems.push(createCopyMenuItem({
                        t,
                        fileData: props.fileResponse,
                        copyFile: storage.copyFile,
                        userState: userState,
                        defaultPermissions,
                        onReload: props.onReload,
                    }));
                    if (props.onManagePermissions) {
                        menuItems.push(createPermissionsMenuItem({
                            onManagePermissions: props.onManagePermissions,
                            fileData: props.fileResponse,
                            onReload: props.onReload,
                            t,
                        }));
                    }
                    menuItems.push({
                        label: t('slideoutMenuItems.labels.versionHistory'),
                        id: fileManagerSlideoutMenuItemsIds.fileHistory,
                        onClick: () => {
                            var _a, _b;
                            props.onSetShowHistory(props.fileResponse.id, (_a = props.fileResponse) === null || _a === void 0 ? void 0 : _a.columns.name, (_b = props.fileResponse) === null || _b === void 0 ? void 0 : _b.columns.currentVersionId);
                        },
                        'data-test': 'version-history-menu-item',
                    });
                }
            }
        }
        return props.customSlideoutMenuItems
            ? props.customSlideoutMenuItems({ menuItems, file: props.fileResponse, reload: props.onReload })
            : menuItems;
    }, [
        props,
        canModify,
        editDialogMenuItem,
        t,
        fileEntityType,
        storage.fileStorageClient,
        storage.copyFile,
        userState,
        defaultPermissions,
        functionClient,
        attachmentError,
    ]);
    const entityId = props.fileResponse.columns.currentVersionId;
    let childDirectoryCount = 0;
    let childFileCount = 0;
    if (children) {
        for (const child of children.rows) {
            if (child.columns.directory) {
                childDirectoryCount++;
            }
            else {
                childFileCount++;
            }
        }
    }
    const allowCheckInFile = allowCheckIn(userState, props.fileResponse.columns);
    const checkInMenuItems = [
        {
            label: t('slideoutMenuItems.labels.checkIn'),
            onClick: () => {
                if (inputFile.current) {
                    inputFile.current.click();
                }
            },
            'data-test': 'checkin-menu-item',
        },
        {
            label: t('slideoutMenuItems.labels.cancelCheckOut'),
            onClick: () => {
                storage.fileStorageClient
                    .post({
                    action: 'partial-update',
                    id: props.fileResponse.id,
                    data: {
                        checkOutUserId: null,
                        checkOutComment: null,
                    },
                })
                    .then(() => {
                    showAlertSnackbar({
                        horizontalPosition: 'center',
                        type: 'success',
                        verticalPosition: 'bottom',
                        autoHideDuration: 2000,
                        message: t('checkOutDialog.cancelCheckOutSnackbar'),
                    });
                    props.onReload();
                    setPreviewImageKey(Date.now());
                })
                    .catch((error) => {
                    handleErrorDialog(error);
                });
            },
            'data-test': 'checkin-cancel-menu-item',
        },
    ];
    const handleDownloadFile = (id) => downloadFile({
        entity: fileVersionEntityName,
        entityId: id,
        key: 'data',
        downloadFileName: props.fileResponse.columns.name,
    });
    return (React.createElement(Grid, { "data-test": 'file-manager-slideout' },
        menuItems.length > 0 && (React.createElement(Box, { sx: { float: 'right' } },
            React.createElement(MenuIconButton, { menuItems: menuItems, "data-test": 'more-button', "aria-label": t('common.more') },
                React.createElement(MoreHorizIcon, null)))),
        React.createElement(Typography, { variant: 'h6', sx: { display: 'flex', wordBreak: 'break-word', marginBottom: 2 }, "data-test": 'file-name-value' }, props.fileResponse.columns.name),
        attachmentError && (React.createElement(Box, { sx: { marginBottom: 2 } },
            React.createElement(Alert, { severity: 'error' },
                React.createElement(Typography, { "data-test": 'attachment-error' }, attachmentError)))),
        !props.fileResponse.columns.directory && !attachmentError && entityId && (React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: '50% 50%', gap: 2, marginBottom: 2, marginRight: 2 } },
            React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => {
                    if (metadata && (!metadata.virusScanResult || metadata.virusScanResult === 'error')) {
                        showConfirmDialog({
                            title: t('documentPanel.fileDownload'),
                            content: (React.createElement(Typography, { "data-test": 'download-dialog-virus-scan-value' }, !metadata.virusScanResult
                                ? 'Virus scan not finished. Are you sure you want to download the file?'
                                : 'Virus scan failed for this file. Are you sure you want to download the file?')),
                            maxWidth: 'sm',
                            confirmLabel: t('documentPanel.download'),
                            onConfirm: () => handleDownloadFile(entityId),
                            onCancel: () => undefined,
                        });
                    }
                    else {
                        handleDownloadFile(entityId);
                    }
                } }, t('documentPanel.download')),
            canWrite && userState.id && !props.fileResponse.columns.checkOutUserId && (React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => {
                    showCheckOutDialog({
                        t,
                        storage: storage.fileStorageClient,
                        id: props.fileResponse.id,
                        currentUserId: userState.id,
                        mobileScreen: downXsScreen,
                        onSuccess: () => props.onReload(),
                    });
                }, "data-test": 'checkout-file-button' }, t('slideoutMenuItems.labels.checkOut'))),
            canWrite && userState.id && props.fileResponse.columns.checkOutUserId && (React.createElement(React.Fragment, null,
                React.createElement(MenuButton, { color: 'primary', variant: 'outlined', disabled: !allowCheckInFile, endIcon: React.createElement(ExpandMoreIcon, null), menuItems: checkInMenuItems, "data-test": 'checkin-button' }, t('slideoutMenuItems.labels.checkIn')),
                React.createElement("input", { ref: inputFile, style: { display: 'none' }, type: 'file', accept: allExtensions, multiple: false, onChange: (event) => {
                        if (event.target.files && event.target.files.length === 1) {
                            const file = event.target.files[0];
                            showFormDialog({
                                title: t('checkInDialog.checkInFile', {
                                    fileName: getShortenedFileName(props.fileResponse.columns.name),
                                }),
                                fullWidth: downXsScreen,
                                config: {
                                    type: 'form',
                                    children: [
                                        {
                                            type: 'form.text',
                                            key: 'checkInComment',
                                            label: t('documentPanel.comment'),
                                        },
                                    ],
                                },
                                data: { checkInComment: props.fileResponse.data.checkOutComment },
                                submitLabel: t('documentPanel.upload'),
                                onSubmit: (data) => {
                                    showCheckInProgressDialog({
                                        t: t,
                                        storage: storage,
                                        fileId: props.fileResponse.id,
                                        file: file,
                                        comment: data.checkInComment,
                                        onSuccess: () => {
                                            props.onReload();
                                            setPreviewImageKey(Date.now());
                                        },
                                    });
                                    return Promise.resolve();
                                },
                            });
                        }
                    }, "data-test": 'checkin-file-input' }))))),
        React.createElement(Grid, { container: true },
            props.fileResponse.columns.checkOutUserId && (React.createElement(React.Fragment, null,
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(Typography, { "data-test": 'check-out-by-label' }, "Check Out By")),
                React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                    React.createElement(Typography, { "data-test": 'check-out-by-value' }, props.fileResponse.columns.checkOutUserDisplayName)),
                props.fileResponse.data.checkOutComment && (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 6 },
                        React.createElement(Typography, { "data-test": 'check-out-comment-label' }, "Check Out Comment")),
                    React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                        React.createElement(Typography, { "data-test": 'check-out-comment-value', sx: { whiteSpace: 'pre-wrap' } }, props.fileResponse.data.checkOutComment)))))),
            React.createElement(Grid, { item: true, xs: 6 },
                React.createElement(Typography, { "data-test": 'owner-label' }, "Owner")),
            React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                React.createElement(Typography, { "data-test": 'owner-value' }, props.fileResponse.columns.ownerDisplayName)),
            props.fileResponse.columns.directory && children && (React.createElement(React.Fragment, null,
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(Typography, { "data-test": 'content-count-label' }, "Contains")),
                React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                    React.createElement(Typography, { "data-test": 'content-count-value' },
                        React.createElement("span", null,
                            childDirectoryCount,
                            " ",
                            childDirectoryCount === 1 ? 'directory' : 'directories',
                            ","),
                        ' ',
                        React.createElement("span", null,
                            childFileCount,
                            " ",
                            childFileCount === 1 ? 'file' : 'files'))))),
            metadata && metadata.contentLength && (React.createElement(React.Fragment, null,
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(Typography, { "data-test": 'size-label' }, "Size")),
                React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                    React.createElement(Typography, { "data-test": 'size-value' }, humanReadableFileSize(metadata.contentLength))))),
            React.createElement(Grid, { item: true, xs: 6 },
                React.createElement(Typography, { "data-test": 'permissions-label' }, "Permissions")),
            React.createElement(Grid, { item: true, xs: 6, sx: { marginBottom: 2 } },
                React.createElement(Typography, { "data-test": 'permissions-value' }, highestPermission)),
            !props.fileResponse.columns.directory && !attachmentError && entityId && isImage(props.fileResponse.columns.type) && (React.createElement(Box, { sx: {
                    boxSizing: 'content-box',
                    display: 'flex',
                    justifyContent: 'center',
                    maxWidth: 320,
                    marginLeft: 'auto',
                    marginBottom: 2,
                    marginRight: 'auto',
                    padding: 1,
                } },
                React.createElement(Image, { key: previewImageKey, entity: fileVersionEntityName, entityId: entityId, attachmentKey: 'data', sx: { maxWidth: 320, maxHeight: 320 }, "data-test": 'preview-img' }))))));
}
function createMoveMenuItem(props) {
    return {
        label: props.t('documentPanel.move'),
        id: fileManagerSlideoutMenuItemsIds.move,
        'data-test': 'move-menu-item',
        onClick: () => showMoveDialog({
            storage: props.storage,
            t: props.t,
            file: props.fileData,
            showRootDirectory: allowWrite({
                userState: props.userState,
                ownerId: null,
                permissions: props.defaultPermissions,
                rootLevel: true,
            }),
            fileEntityType: props.fileEntityType,
            onSuccess: () => props.onReload(true),
        }),
    };
}
function createArchiveFileMenuItem(params) {
    return {
        label: params.t('documentPanel.archive'),
        id: fileManagerSlideoutMenuItemsIds.archive,
        'data-test': 'archive-file-menu-item',
        onClick: () => showArchiveDialog(params),
    };
}
function createPermissionsMenuItem(props) {
    return {
        label: props.t('documentPanel.manageAccess'),
        id: fileManagerSlideoutMenuItemsIds.manageAccess,
        'data-test': 'permissions-menu-item',
        onClick: () => {
            if (props.onManagePermissions) {
                props.onManagePermissions(props.fileData, () => props.onReload(true));
            }
        },
    };
}
function createCopyMenuItem(props) {
    return {
        label: props.t('documentPanel.copy'),
        id: fileManagerSlideoutMenuItemsIds.copyFile,
        onClick: () => showCopyDialog({
            copyFile: props.copyFile,
            t: props.t,
            file: props.fileData,
            showRootDirectory: allowWrite({
                userState: props.userState,
                ownerId: null,
                permissions: props.defaultPermissions,
                rootLevel: true,
            }),
            onSuccess: () => props.onReload(),
        }),
        'data-test': 'menu-item-copy',
    };
}
function isImage(contentType) {
    return contentType === 'image/png' || contentType === 'image/jpeg';
}
function getShortenedFileName(fileName) {
    if (fileName.length > 20) {
        return fileName.substr(0, 10) + '...' + fileName.substr(-8);
    }
    return fileName;
}
